import * as React from 'react'
import { Pressable, StyleSheet, View } from 'react-native'
import { colors } from './theme'
import { Text } from './Text'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { Stack } from './Stack'
import { Inline } from './Inline'

interface CheckboxInputProps {
	label: string
	selected: boolean
	onPress: () => void
	disabled?: boolean
	description?: string
	error?: string
	color?: string
	value?: string
	name?: string
	children?: React.ReactNode
	alignItems?: 'flex-start' | 'center' | 'flex-end'
}

export const CheckboxInput = React.forwardRef<View, CheckboxInputProps>(
	(
		{
			label,
			selected,
			onPress,
			disabled = false,
			description,
			error,
			color = global.orgColor,
			value,
			name,
			children,
			alignItems = 'center',
		},
		ref
	) => {
		const [isFocused, setIsFocused] = React.useState(false)

		return (
			<Pressable
				onPress={onPress}
				disabled={disabled}
				style={styles.container}
				accessibilityRole="checkbox"
				accessibilityState={{ checked: selected, disabled }}
				onFocus={() => setIsFocused(true)}
				onBlur={() => setIsFocused(false)}
			>
				<Stack align="left" gap="xs">
					<Inline alignX="left">
						<View
							style={[
								styles.checkbox,
								disabled && styles.checkboxDisabled,
								error && styles.checkboxError,
							]}
						>
							<MaterialCommunityIcons
								name={selected ? 'checkbox-marked' : 'checkbox-blank-outline'}
								size={24}
								color={selected ? color : colors.gray300}
							/>
						</View>
						<View style={[styles.content, { alignItems }]}>
							<Stack align="left">
								{children ? (
									<View style={{ flex: 1 }} aria-label={label}>
										{children}
									</View>
								) : (
									<Text
										level="body"
										weight="regular"
										tone={disabled ? 'secondary' : 'neutral'}
										style={{ flex: 1 }}
									>
										{label}
									</Text>
								)}
								{description && (
									<Text level="small" tone="secondary" style={styles.description}>
										{description}
									</Text>
								)}
							</Stack>
						</View>
					</Inline>
					{error ? (
						<Text level="small" tone="critical">
							{error}
						</Text>
					) : null}
				</Stack>
			</Pressable>
		)
	}
)

CheckboxInput.displayName = 'CheckboxInput'

const styles = StyleSheet.create({
	container: {
		flexDirection: 'row',
		padding: 4,
		gap: 8,
	},
	checkbox: {
		width: 24,
		height: 24,
		alignItems: 'center',
		justifyContent: 'center',
	},
	checkboxDisabled: {
		opacity: 0.5,
	},
	checkboxError: {
		borderColor: colors.red500,
	},
	content: {
		flex: 1,
		flexDirection: 'row',
		flexWrap: 'wrap',
	},
	description: {
		marginTop: 4,
	},
	error: {
		marginLeft: 2,
	},
})
